/**
 * Two nudges, deliberately quieter than the check-in prompt.
 *
 * Non-modal on purpose. Check-ins now fire every ten minutes and their modal
 * cannot be dismissed, because ignoring one counts as a miss. Stacking more
 * blocking dialogs on top of that is how an alert system gets trained away, so
 * these sit in a corner, take a click, and never take the screen.
 *
 * They also never render while a check-in is showing. A card sliding in over a
 * dialog somebody is not allowed to dismiss is noise at the exact moment
 * attention is already spoken for.
 *
 * Nothing here decides what needs attention. Both lists come from the server,
 * and the follow-up one is the Followup table an interested call already
 * writes to — so this shows a record that exists rather than a second opinion
 * about who needs chasing.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { alertTone, type AlertTier } from '../lib/checkInAlarm';
import { formatPhone, telHref } from '../lib/phone';
import {
  BellRing,
  CalendarClock,
  FileText,
  Loader2,
  UserCheck,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import { client, errorMessage } from '../api/client';
import type { Alerts, PendingPing } from '../types';
import { Link as RouterLink } from 'react-router-dom';

const POLL_MS = 60_000;

/**
 * How many cards are on screen at once.
 *
 * Not an interruption cap — these do not interrupt. It is so a recruiter with
 * thirty overdue followups sees a corner of the screen rather than a wall of
 * it; the rest stay one click away on My Day, which is the list built for
 * working through them.
 */
const VISIBLE = 3;

function clock(value: string): string {
  return new Date(value).toLocaleTimeString('en-IN', {
    hour: 'numeric',
    minute: '2-digit',
  });
}

export default function AlertWatcher() {
  const queryClient = useQueryClient();

  /**
   * Observes the check-in query without owning it.
   *
   * Same key and same fetcher as PingWatcher, so TanStack shares one cache
   * entry — this re-renders when a prompt appears but never issues a request
   * of its own. `enabled: false` is what keeps the polling in one place.
   */
  const { data: ping } = useQuery<PendingPing>({
    queryKey: ['pending-ping'],
    queryFn: async () => (await client.get('/api/v1/attendance/ping/pending')).data,
    enabled: false,
  });
  const checkInShowing = Boolean(ping?.ping);

  const { data } = useQuery<Alerts>({
    queryKey: ['alerts'],
    queryFn: async () => (await client.get('/api/v1/alerts')).data,
    refetchInterval: POLL_MS,
    refetchOnWindowFocus: true,
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['alerts'] });
    void queryClient.invalidateQueries({ queryKey: ['my-day'] });
  };

  const ackJoining = useMutation({
    mutationFn: async (id: string) =>
      (await client.post(`/api/v1/placements/${id}/followup-ack`)).data,
    onSuccess: refresh,
    onError: (e) => toast.error(errorMessage(e)),
  });

  const acknowledge = useMutation({
    mutationFn: async ({ id, dayBefore }: { id: string; dayBefore: boolean }) =>
      (
        await client.post(
          `/api/v1/interviews/${id}/acknowledge`,
          undefined,
          { params: { dayBefore } },
        )
      ).data,
    onSuccess: refresh,
    onError: (e) => toast.error(errorMessage(e)),
  });

  /**
   * Resume cards hidden for this session only.
   *
   * Deliberately not persisted. The card exists until the resume status moves,
   * and a stored dismissal would let it be silenced without recording what
   * happened — which is the thing it is asking for. Closing it clears the
   * screen now; it returns on the next load until the status says otherwise.
   */
  const [hidden, setHidden] = useState<Set<string>>(new Set());

  const resumes = (data?.resumeDeadlines ?? []).filter((r) => !hidden.has(r.id));
  const interviews = data?.interviews ?? [];
  const joining = data?.joiningFollowups ?? [];
  const total = resumes.length + interviews.length + joining.length;

  /**
   * One tone per card, the first time that card is seen.
   *
   * Delayed rather than suppressed while a check-in is up. The cards do not
   * render then, so nothing is marked as seen, and whatever was waiting sounds
   * when the modal closes. Suppressing instead would mean an alert that
   * arrived during a check-in was never announced at all.
   *
   * When several arrive together only the most urgent sounds. Three tones at
   * once is a chord, not three signals, and the ear cannot pick them apart —
   * so the loudest thing speaks for the batch.
   */
  const heard = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (checkInShowing) return;

    const arriving: { id: string; tier: AlertTier }[] = [
      ...interviews.map((i) => ({ id: i.id, tier: 'urgent' as const })),
      ...joining.map((j) => ({ id: j.id, tier: 'high' as const })),
      ...resumes.map((r) => ({ id: r.id, tier: 'moderate' as const })),
    ];

    const fresh = arriving.filter((a) => !heard.current.has(a.id));
    if (fresh.length === 0) {
      // Forget anything that has gone, so a card that returns is announced
      // again rather than arriving in silence weeks later.
      heard.current = new Set(arriving.map((a) => a.id));
      return;
    }

    const rank: Record<AlertTier, number> = { moderate: 0, high: 1, urgent: 2 };
    const loudest = fresh.reduce((worst, a) =>
      rank[a.tier] > rank[worst.tier] ? a : worst,
    );
    alertTone(loudest.tier);

    heard.current = new Set(arriving.map((a) => a.id));
  }, [checkInShowing, interviews, joining, resumes]);

  if (checkInShowing || total === 0) return null;

  // Ordered by how soon they stop being actionable: an interview is about to
  // start, a followup has already waited, and a joining check-in has a
  // fortnight of slack in it.
  const shown = [
    ...interviews.map((i) => ({ kind: 'interview' as const, item: i })),
    ...resumes.map((r) => ({ kind: 'resume' as const, item: r })),
    ...joining.map((j) => ({ kind: 'joining' as const, item: j })),
  ].slice(0, VISIBLE);

  /**
   * Centred and prominent, and still not a modal.
   *
   * The distinction is load-bearing. The check-in dialog has a backdrop that
   * covers the page and swallows every click, because ignoring it has to be
   * impossible. These must not: the page underneath stays fully usable, so
   * there is deliberately no backdrop element at all.
   *
   * pointer-events-none on the wrapper is what makes that true. The wrapper
   * spans the viewport in order to centre its children, and without it every
   * click in the middle of the screen would land on an invisible div. Only the
   * cards themselves take pointer-events-auto, so clicks pass through the gaps
   * around them to whatever is behind.
   *
   * What a centred card genuinely does block is the pixels it covers, and
   * these were made deliberately large — an alert that gets missed is worth
   * nothing, so the cards are sized to be the first thing seen rather than to
   * stay out of the way. The three-card limit and the viewport cap are what
   * stop that from covering the whole screen, and they matter more at this
   * size than they did at the old one.
   */
  return (
    <div className="pointer-events-none fixed inset-0 z-20 flex flex-col items-center justify-start gap-5 pt-20">
      {shown.map((entry) =>
        entry.kind === 'joining' ? (
          <div
            key={entry.item.id}
            className="ats-glow-high pointer-events-auto w-[44rem] max-w-[calc(100vw-2rem)] rounded-2xl bg-white p-8"
          >
            <div className="flex items-start gap-4">
              <BellRing className="mt-1 h-10 w-10 shrink-0 animate-pulse text-red-600" />
              <UserCheck className="mt-1 h-8 w-8 shrink-0 text-orange-600" />
              <div className="min-w-0 flex-1">
                <p className="text-xl font-semibold text-gray-900">
                  {entry.item.day} days since{' '}
                  {entry.item.candidateName ?? 'they'} joined
                </p>
                <p className="truncate text-base text-gray-600">
                  {entry.item.clientName ?? 'Client'} · check in with them
                </p>
                {entry.item.candidatePhone && (
                  <a
                    href={`tel:${telHref(entry.item.candidatePhone)}`}
                    className="whitespace-nowrap text-base text-indigo-600 hover:underline"
                  >
                    {formatPhone(entry.item.candidatePhone)}
                  </a>
                )}
              </div>
              {/* Dismisses until the next fortnight and says nothing about
                  whether the call happened. */}
              <button
                type="button"
                className="btn-ghost p-2"
                aria-label="Dismiss"
                disabled={ackJoining.isPending}
                onClick={() => ackJoining.mutate(entry.item.id)}
              >
                {ackJoining.isPending ? (
                  <Loader2 className="h-6 w-6 animate-spin" />
                ) : (
                  <X className="h-6 w-6" />
                )}
              </button>
            </div>
          </div>
        ) : entry.kind === 'interview' ? (
          <div
            key={entry.item.id}
            className="ats-glow-urgent pointer-events-auto w-[44rem] max-w-[calc(100vw-2rem)] rounded-2xl bg-white p-8"
          >
            <div className="flex items-start gap-4">
              <BellRing className="mt-1 h-10 w-10 shrink-0 animate-pulse text-red-600" />
              <CalendarClock className="mt-1 h-8 w-8 shrink-0 text-red-600" />
              <div className="min-w-0 flex-1">
                <p className="text-xl font-semibold text-gray-900">
                  Interview{' '}
                  {entry.item.dayBefore ? 'tomorrow' : ''}
                  {entry.item.scheduledAt
                    ? ` at ${clock(entry.item.scheduledAt)}`
                    : entry.item.dayBefore
                      ? ''
                      : ' today'}
                </p>
                <p className="truncate text-base text-gray-600">
                  {entry.item.candidateName ?? 'Candidate'}
                  {entry.item.clientName ? ` · ${entry.item.clientName}` : ''}
                  {entry.item.roundNumber > 1 ? ` · round ${entry.item.roundNumber}` : ''}
                </p>
              </div>
              {/* Dismisses the reminder and nothing else. Whether the
                  interview happened is recorded on the interview itself. */}
              <button
                type="button"
                className="btn-ghost p-2"
                aria-label="Dismiss"
                disabled={acknowledge.isPending}
                onClick={() =>
                  acknowledge.mutate({
                    id: entry.item.id,
                    dayBefore: entry.item.dayBefore,
                  })
                }
              >
                {acknowledge.isPending ? (
                  <Loader2 className="h-6 w-6 animate-spin" />
                ) : (
                  <X className="h-6 w-6" />
                )}
              </button>
            </div>
          </div>
        ) : (
          <div
            key={entry.item.id}
            className={`pointer-events-auto w-[44rem] max-w-[calc(100vw-2rem)] rounded-2xl bg-white p-8 ${
              entry.item.overdue ? 'ats-glow-urgent' : 'ats-glow-moderate'
            }`}
          >
            <div className="flex items-start gap-4">
              <BellRing className="mt-1 h-10 w-10 shrink-0 animate-pulse text-red-600" />
              <FileText className="mt-1 h-8 w-8 shrink-0 text-amber-600" />
              <div className="min-w-0 flex-1">
                <p className="text-xl font-semibold text-gray-900">
                  {entry.item.overdue ? 'Resume overdue' : 'Resume due'} —{' '}
                  {entry.item.candidateName ?? 'a candidate'}
                </p>
                {/* The deadline is stated, not implied. "Due soon" tells
                    somebody to hurry; a time tells them how much. */}
                <p className="text-base text-gray-600">
                  {entry.item.overdue ? 'Was due ' : 'By '}
                  {new Date(entry.item.dueAt).toLocaleString('en-IN', {
                    day: 'numeric',
                    month: 'short',
                    hour: 'numeric',
                    minute: '2-digit',
                  })}
                </p>
                {/* The number is on the card because a nudge that does not say
                    who to ring is a nudge somebody has to go and research. */}
                {entry.item.candidatePhone && (
                  <a
                    href={`tel:${telHref(entry.item.candidatePhone)}`}
                    className="whitespace-nowrap text-base text-indigo-600 hover:underline"
                  >
                    {formatPhone(entry.item.candidatePhone)}
                  </a>
                )}
                {/* No dismiss button, deliberately. This card goes when the
                    resume status moves, so the way to clear it is to record
                    what happened — which is the work rather than a dismissal
                    of it. */}
                <RouterLink
                  to="/candidates"
                  className="mt-2 block text-base text-gray-500 hover:text-indigo-700 hover:underline"
                >
                  Record it, or set a new deadline
                </RouterLink>
              </div>
              {/* Clears it from view for now. Unlike the other two cards this
                  records nothing: there is no "seen" state for a resume that
                  has not arrived, and inventing one would let the card be
                  silenced without the resume ever turning up. */}
              <button
                type="button"
                className="btn-ghost p-2"
                aria-label="Hide for now"
                title="Hide for now — returns until the resume status is updated"
                onClick={() =>
                  setHidden((current) => new Set(current).add(entry.item.id))
                }
              >
                <X className="h-6 w-6" />
              </button>
            </div>
          </div>
        ),
      )}

      {/* No destination claimed, because the three kinds live in three
          places: a resume chase is on Interview Candidates, an interview on
          Interviews, a joining check-in on Placements. It used to say "more on
          My Day", which was true only of the followup cards this replaced. */}
      {total > shown.length && (
        <p className="pointer-events-auto rounded-lg bg-gray-900/90 px-5 py-2.5 text-center text-base text-white shadow-lg">
          {total - shown.length} more waiting
        </p>
      )}
    </div>
  );
}
